<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { format } from 'svelte-i18n';
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/components/Chart/chart_config';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { hasAddonPeripherals, getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { LabelListV2, ParameterCardV2 } from '@Shelly/shelly-design-system';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import ControlVentoraBoost from 'src/components/Controls/Single/ControlVentoraBoost.svelte';
	import ControlVentoraFanSpeed from 'src/components/Controls/Single/ControlVentoraFanSpeed.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import ThumbDeviceChartV2 from 'src/components/Chart/ThumbDeviceChartV2.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import type { Device } from 'src/types/Device.ts';

	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: isVentoraFanAuto = status?.['fan:0']?.mode === 'auto';

	$: _statuses = status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: labelList = Array.isArray(_statuses)
		? _statuses.filter((e) => {
				return 'undefined' === typeof e.main && e.name !== 'state';
			})
		: [];
	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	const handleOnClickThumbChart = (event: CustomEvent | Event) => {
		navigateToHistoryStatisticsTab();
	};

	$: addonPeripherals = hasAddonPeripherals(device) ? getAddonStatus(device, [], false, true) : [];
	$: switchAddon = addonPeripherals.find((p) => p.peripheralType === 'switch');

	let timerLabels: Record<string, any> = {};
	let timer = false;
	let deviceState: any = getDeviceState(device?.id);

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			timer =
				isGen2 && status?.[`${get_gen_2_component_channel_structure(device)}`]?.timer_started_at;
		}
		deviceState = getDeviceState(device?.id);
	});
</script>

<DeviceViewStateWrapper {device}>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		{#if timer}
			{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
				<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}
		<div class="lable-list-wrapper-in-drawer-tab-content mb-s">
			<LabelListV2
				a11yProps={{ isInteractive: false }}
				backgroundColor="--color-0"
				labelList={combinedLabelList}
				size="m"
				data-prevent-drawer-swipe
				data-prevent-sidepanel-swipe
				isDefaultCursor
				on:label-click={(e) => {
					e.stopPropagation();
				}}
			/>
		</div>
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div slot="content" class="min-height-0 d-flex flex-direction-column justify-content-center">
		{#if deviceChartsConfig.length}
			<ThumbDeviceChartV2 {device} {deviceChartsConfig} on:click={handleOnClickThumbChart} />
		{/if}
	</div>

	<div
		class="align-items-center justify-content-center d-flex flex-direction-column min-height-0"
		slot="staticBottom"
	>
		{#if switchAddon}
			<div class="d-flex gap-s">
				<div class="control-flex-column flex-1">
					<ControlOnOffAddon
						{device}
						{status}
						isInSlot
						useV2Components={true}
						component={switchAddon.sensor?.component}
					/>
				</div>
				<div class="control-flex-column flex-1">
					{#if isVentoraFanAuto}
						<ControlVentoraBoost
							{device}
							{status}
							isInSlot
							useV2Components={true}
							deviceButtonV2Props={{ size: 'l' }}
						/>
					{/if}
				</div>
			</div>
		{:else if isVentoraFanAuto}
			<ControlVentoraBoost
				{device}
				{status}
				isWide
				useV2Components={true}
				deviceButtonV2Props={{ size: 'l' }}
			/>
		{:else if !isVentoraFanAuto}
			<div class="flex-full-height justify-content-center">
				<ControlVentoraFanSpeed {device} {status} class="flex-full-height" isNewLayout={true} />
			</div>
		{/if}
	</div>
</DeviceViewStateWrapper>
